/**
 * The projection engine's own version, independent of the npm package version.
 *
 * Hand-maintained SemVer: bump it whenever a change would alter the numeric
 * output of {@link project} for the same input (a "projection-breaking" change).
 * It is a compile-time constant — never read from `package.json` at runtime,
 * which would add IO and non-determinism. The persistence layer (Stage F) stamps
 * this onto every stored `projection_run` so a projection can be reproduced from
 * its snapshot under the exact engine that produced it.
 */
/**
 * 3.0.0 (2026-07-16): unscheduled pension drawdown taxed through the HMRC
 * bands (was a flat pre-draw marginal estimate, 0% below the personal
 * allowance); today's-value income streams and spending items escalate from
 * the projection start (was: from their own start year); pension draw income
 * now consumes basic-rate CGT headroom. Derivation:
 * docs/tax/golden-handcalc-derivation.md.
 */
/**
 * 3.1.0 (2026-07-16): insolvency surfaced first-class (`insolvency` block,
 * per-year `cumulativeUnmetShortfall` + `netWorthAfterShortfall`,
 * `insolvent-with-illiquid-assets` warning); per-year cumulative `deflatorPpm`
 * for the today's-value view; `implausible-growth-rate` warning above the
 * 2,000 bps ceiling. Invariant-harness fixes: negative (bear-case) cash rates
 * no longer produce negative taxable savings income or a -1p pot (taxable
 * slice floored at zero; pot credit clamped at the balance), and compounding
 * past the exact-integer pence range now fails loud with a named-year error
 * instead of silently losing pennies. No numeric change to any golden path —
 * all five masters byte-identical (docs/engine/projection-runaway.md;
 * disposition: docs/security/engine-3.1.0-disposition.md).
 */
/**
 * 3.2.0 (2026-07-27): income streams may name their source asset
 * (`sourceAssetId`). Consumed for rental streams only: rent linked to a
 * property is paid only while that property is held (a planned sale stops it
 * from the sale year); rent from a JOINTLY-held linked let passes to the
 * surviving partner, matching the yield path's `aliveOwners`; the linked
 * let's mortgage interest is credited as a Section 24 finance cost, shared
 * across the streams paid against the same property; a link naming an asset
 * not on the plan raises `rental-source-missing` and is ignored. Documents
 * without the field are numerically unchanged (parity-tested).
 */
/**
 * 3.3.0 (2026-07-31): annual-allowance carry-forward modelled within the
 * projection. Unused annual allowance from up to the previous three PLAN
 * years (oldest first, current year's own allowance consumed before any
 * carried amount) now offsets an excess before the annual-allowance charge
 * is estimated; unused allowance from years before the plan start stays nil
 * (`aa-carry-forward-pre-plan-nil` replaces `aa-carry-forward-not-modelled`).
 * Carry-forward is bypassed and the buffer cleared once the MPAA is
 * triggered. Projections that never exceeded the allowance are numerically
 * unchanged. Tests: household-aa-carry-forward.test.ts.
 * Also: the per-year trace now itemises spending (`spendingLines`) and
 * one-off life-event outflows (`lifeEventOutflows`), and explainYear() reads
 * them out — the "why is spending £X this year" drill-down. Year rows and
 * balances are unaffected; only the trace payload grew.
 */
/**
 * 3.4.0 (2026-08-08): the per-year trace itemises debt (`debtLines`): each
 * liability's opening balance, interest, amount paid and closing balance,
 * with a `cleared` marker naming the year — and the way — a balance reaches
 * zero (final scheduled payment, remaining balance falling due at end of
 * term, or repayment from a linked property sale). explainYear() reads the
 * lines out, so a payoff-year debt-payment spike explains itself. Year rows,
 * audit buckets and balances are unaffected; only the trace payload grew
 * (goldens regenerated, headline figures byte-identical).
 * Tests: household-debt-lines.test.ts.
 */
/**
 * 3.5.0 (2026-08-24): a liability may carry `interestRateBps: null` ("rate
 * not yet provided") — it is excluded from the projection entirely (no
 * interest, no payments, no `liabilitiesOutstanding`) and raises a standing
 * `liability-rate-needed` warning; it is never modelled at 0%. Inputs with
 * every rate stated are numerically unchanged. The `depleted` warning's
 * wording is now year-less (the year travels in its `year` field) so a
 * multi-year shortfall collapses to one ranged modelling note.
 * Tests: household-rate-needed.test.ts.
 */
/**
 * DELIBERATELY NOT BUMPED (2026-09-17) for historical replay (GOAL 2026-09-17 HS-3).
 *
 * New OPTIONAL inputs only: a market-shock path step may carry `returnBpsByWrapper`
 * (a year's total return replacing effective growth, yield and timed delta) and
 * `economy` (CPI, RPI — or `null` for no RPI — earnings and the savings rate for
 * that year), and a shock may carry `liabilityRatePaths`. With none of them
 * present — every input that could previously be expressed — not one figure or
 * serialised byte moves: the golden masters and the additive archive proofs are
 * unchanged, and that is the evidence. An older engine REFUSES these fields at
 * the wire (strict schemas), so no stored run can replay to different figures
 * under this version label.
 */
/**
 * DELIBERATELY NOT BUMPED (2026-08-26) for student loan repayments (step 9b).
 *
 * The bump rule at the top of this file is mechanical: bump when a change
 * would alter the numeric output of the projection FOR THE SAME INPUT. This
 * change cannot. `studentLoans` is a new optional person field; with it absent
 * — which is every input that could previously be expressed — not one figure,
 * and not one serialised byte, moves. The unmoved golden masters are the
 * evidence, and they are the reason the version stays put: bumping it would
 * flip all six hashes and destroy exactly the proof that the repayment landed
 * OUTSIDE `totalTax`.
 *
 * Nothing is lost in attribution either: a run carrying a student loan figure
 * could only have been produced by an engine that models one.
 */
/**
 * 3.6.0 (2026-09-04): client goals. The surplus waterfall gains a `jisa`
 * target — a fund-if-surplus step that credits one dependent's Junior ISA out
 * of the year's surplus at step 10 (never at contributions), sharing the
 * child's statutory limit with any step-6 subscription and stopping at 18;
 * it never draws on a pot and is unfunded in a deficit year. The year row
 * gains two ADDITIVE fields: `balancesByAsset` (closing balance per
 * individually-tracked asset) and `sweepSteps` (the sweep's own per-step
 * ledger, present only when a sweep is configured). Plans without a `jisa`
 * step are numerically unchanged: golden-masters.additive.test.ts proves the
 * regenerated goldens equal the 3.5.0 goldens byte-for-byte once the two new
 * fields are stripped. Tests: household.jisa-sweep.test.ts.
 */
/**
 * 3.7.0 (2026-09-04): client goals v1.5 + v2. Three further surplus-step
 * targets: `pension` may be restricted to one person (a goal's "extra into my
 * pension"); `spend` pays a goal's amount out of surplus (a one-off or yearly
 * outflow on the life-event bucket and trace line; `isGift` joins the
 * inheritance-tax gift list); `liability` overpays a named debt out of
 * surplus (balance, debt line, `debtRepaid` / `debtService` audit buckets and
 * the year's `netCashFlow` all reflect it). Goal steps may carry a start year.
 * The year row gains ADDITIVE `balancesByLiability`. Plans without the new
 * targets are numerically unchanged: golden-masters.additive.test.ts proves
 * the six 3.5.0 goldens still match once the three additive fields are
 * stripped. Tests: household.goal-targets.test.ts.
 */
/**
 * 3.8.0 (2026-09-05): inheritance tax by year of death (GOAL 2026-09-05
 * A1.3). `estimatedIht` gains two ADDITIVE fields: `byYearOfDeath` (the same
 * assessment restated as if death fell in each projection year — one
 * inheritanceTax() call per year on the settings as applied at the headline)
 * and `gifts` (every gift made by the headline year with the exemption,
 * years survived, taper and tax the headline assessment gave it), plus
 * `applied` (the residence band after taper, and whether the spouse
 * exemption, transferred allowances and charity rate were applied). The tax
 * layer's `inheritanceTax()` result gains `giftDetails`. No figure moves:
 * golden-masters.additive.test.ts proves the regenerated goldens equal the
 * 3.7.0 goldens byte-for-byte once the three new fields are stripped. Tests:
 * iht-by-year.test.ts.
 */
/**
 * 3.9.0 (2026-09-05): stress paths (GOAL 2026-09-05 C1.1). A market shock may
 * now be an anchored SIGNED multi-year `path` — `anchor: { year }` or
 * `{ yearsAfterRetirement, person }`, each step a `returnDeltaBpsByWrapper`
 * added to that year's growth (a fall negative, a recovery year positive).
 * The anchor is resolved once at setup from the people as the projection sees
 * them, so a scenario that moves a retirement age moves the window. The legacy
 * `year` + `dropBpsByWrapper` form is a single-element negative path and is
 * byte-identical (market-shock-path.test.ts); no existing golden moves — the
 * 3.7.0 additive proof still holds — and a ninth golden, `stress-path`, pins a
 * fall-and-recovery path. Warnings: a path wholly outside the window keeps
 * `market-shock-outside-window`; a partly-outside path warns which years took
 * effect.
 */
/**
 * 3.10.0 (2026-09-06): a sold business stops paying (GOAL 2026-09-05 W1.1).
 * An income stream whose `sourceAssetId` names a BUSINESS interest is now
 * consumed the same way a rental stream's property link already was: once a
 * planned `businessSale` has actually run, that salary or dividend stops from
 * the sale year and `business-sale-stream-truncated` names the stream, the
 * sale and the year. A sale that was skipped for want of value
 * (`planned-sale-skipped`) truncates nothing. Separately, a business sale on
 * an asset with NO recorded `costBasis` raises `cost-basis-missing` and states
 * that the whole proceeds were treated as the gain — the figure is unchanged
 * (it was always `proceeds - (costBasis ?? 0)`), only now it is stated; an
 * explicit £0 basis is a stated figure and stays silent.
 *
 * VALUES CAN MOVE, so this is a minor bump, not a patch: any plan that links
 * an income stream to a business it later sells loses that income from the
 * sale year. No golden moves — none of the nine fixtures links a stream to a
 * business, and the only fixture that sells one records its basis — so the
 * nine 3.9.0 goldens are kept under `golden/v3.9.0/` and
 * golden-masters.additive.test.ts proves each fresh result equals its 3.9.0
 * file byte for byte once the version label is restored.
 * Tests: business-sale-streams.test.ts, business-sale-cost-basis.test.ts.
 */
/**
 * 3.11.0 (2026-09-06): gift exemptions (GOAL 2026-09-05 A2.1). The annual gift
 * exemption is applied PER TAX YEAR with one year's carry-forward, the current
 * year's own spent first, where it had been a single pool drained across a
 * whole lifetime of gifts; a marriage/civil-partnership exemption is taken
 * before it (£5,000 parent / £2,500 grandparent / £1,000 other) when the
 * adviser has asserted the occasion; and the engine now hands the tax layer
 * gifts up to EIGHT years before the assessment rather than seven, because a
 * gift at year 7 is untaxed but still spends its own year's allowance.
 *
 * VALUES MOVE for any plan carrying a lifetime gift — a client gifting £3,000 a
 * year for ten years had been given £3,000 of exemption where HMRC gives
 * £30,000, overstating the estate estimate by roughly £7,200. Seven existing
 * expectations moved, all in that direction.
 *
 * No golden moves, but that is NOT why this is a minor bump: the rule is about
 * INPUTS, and none of the nine fixtures happens to carry a lifetime gift. Two
 * stored runs with identical snapshots and the same version label would
 * otherwise return different inheritance-tax figures, so a reissued report
 * would silently disagree with the one a client is holding. The 3.10.0 goldens
 * are kept under `golden/v3.10.0/`. Tests: inheritance-tax-annual-exemption,
 * inheritance-tax-wedding-gifts, gift-ledger. Evidence:
 * docs/tax/iht-gifts-exemptions.md.
 */
/**
 * 3.12.0 (2026-09-08): A2.7 + A2.8 + A2.14 — the half of A2.1 that did not
 * ship. A RECURRING spending line can be flagged as a gift and reaches the
 * seven-year clock once per active year at the amount actually given that year
 * (`isGift` on `spending`), so "£500 a month to the grandchildren" is one
 * record rather than ten life events — or, as before, a spending line that
 * reduced the wealth on the chart while the gifts page told the client no
 * gifts had been recorded. `exemptionClaimed: 'annual' | 'none'` lets an
 * adviser state that a gift draws no annual exemption, in which case it
 * consumes none either and the year's £3,000 stays available to the gift that
 * needs it. `iht.priorYearExemptionUsed` replaces the walk's one blind
 * assumption — that the tax year above the earliest recorded gift was entirely
 * unused — and while it is unstated the engine now says so
 * (`gift-exemption-pre-plan-unused`).
 *
 * Every addition is opt-in: absent flags reproduce 3.11.0 exactly, and no
 * golden fixture carries a gift, so all nine masters are byte-identical
 * (asserted in golden-masters.additive.test.ts). The bump is on the INPUT
 * rule: two runs stamped 3.11.0 could otherwise return different
 * inheritance-tax figures. The 3.11.0 goldens are kept under `golden/v3.11.0/`.
 * Tests: gift-recurring, gift-exemption-pre-plan,
 * inheritance-tax-exemption-claimed, gift-ledger-contract.
 */
/**
 * 3.13.0 (2026-09-08): A2.2 — normal expenditure out of income, as an
 * adviser-ASSERTED position the engine never tests. `exemptionClaimed` gains
 * `'normalExpenditureOutOfIncome'`, the value A2.8 deliberately refused to
 * accept until there was an implementation behind it, and it is different in
 * kind from the other two: under IHTA 1984 s.21 such a gift is not a
 * potentially exempt transfer at all, so it leaves the seven-year clock
 * entirely — no charge, no nil-rate band consumed, no taper, no marriage
 * exemption drawn, and no annual exemption spent, that allowance staying
 * available to the gifts that need it. The gift is still reported, carrying
 * `normalExpenditureAsserted` so a document can tell it apart from a gift that
 * merely aged out of the window (the two produce an identical £0 for opposite
 * reasons). A free-text note recording the basis is required by the contract
 * (D-009 N-A2).
 *
 * Opt-in, so absent flags reproduce 3.12.0 exactly — but the readout gains a
 * field on EVERY gift row, so the goldens move by that field alone and the
 * additive proof strips it. The 3.12.0 goldens are archived under
 * `golden/v3.12.0/`. Tests: inheritance-tax-normal-expenditure,
 * gift-normal-expenditure, normal-expenditure-contract,
 * normal-expenditure-report.
 */
/**
 * 3.14.0 (2026-09-08): A2.3 — `estimatedIht.composition`, what the assessed
 * estate is actually made of. The client reviewer raised the same gap at A2.1,
 * A2.7 and A2.10: one estate figure with no way to see what is in it, what was
 * left out, or why — and the estate table's own arithmetic failed for that
 * reason at A2.10.
 *
 * A restatement of the arithmetic the assessment already does, never a second
 * calculation: counted lines − business relief − debts = `estateValue`, pinned
 * to the penny. Wrappers the plan does not hold are omitted; the children's
 * Junior ISA and (per the rule year that applied) pensions carry the reason
 * they are out; the business stays in at full value with the relief shown
 * coming off, because a smaller business would misdescribe what the client
 * owns. No beneficiaries and no will terms — neither is modelled.
 *
 * Purely additive: the field is new, no figure moves, and every archived proof
 * strips it. The 3.13.0 goldens are archived under `golden/v3.13.0/`. Tests:
 * iht-estate-composition.
 */
/**
 * 3.15.0 (2026-09-08): A2.4 (FG-F4-02) — grandparent-paid education fees.
 *
 * `paidBy` and `ihtTreatment` on both education kinds. `paidBy: 'thirdParty'`
 * removes the fees from this household's cash flow entirely — modelling
 * somebody else's outflow as your own depletes a plan that is fine.
 * `ihtTreatment: 'gift' | 'normalExpenditureOutOfIncome'` routes each year's fee
 * into the seven-year clock at the amount actually paid, riding the gift flag
 * A2.7 already put on a spending line rather than adding a second
 * gift-collection path. The s.21 value means exactly what it means on a gift
 * (A2.2) and carries the same required note.
 *
 * NOT purely additive, and this is the one to read carefully: a plan with
 * education fees and an inheritance-tax estimate now raises
 * `education-fees-not-assessed-as-gift` by DEFAULT, so `couple-private-school`
 * gains a warning code. No figure on it moves — the additive proofs strip the
 * new warning and hash byte-identical to every earlier archive, which is what
 * makes them proofs about figures rather than about warning lists.
 *
 * That warning is adviser-facing on purpose: the tool cannot tell a grandparent
 * paying a grandchild's fees from a parent paying their own child's, and for the
 * parent a note about the seven-year gift rules on their child's school fees is
 * noise about a question that does not arise. `education-fees-third-party` IS
 * client-facing — it explains money the client can see is not leaving the plan.
 *
 * The 3.14.0 goldens are archived under `golden/v3.14.0/`. Tests:
 * education-fees-gift.
 */
/**
 * 3.16.0 (2026-09-08): A2.5a — the IM-F7-01 survivor gate, closed as ONE
 * deliverable rather than four tickets, which is how the register insisted on
 * it: two of the four defects overstated the survivor's position and two
 * understated it, so fixing three leaves a run whose net direction of error
 * nobody can state.
 *
 *  - IM-F2-03: a solely-owned let's rent stopped the year its owner died, while
 *    the property passed to the survivor and carried on growing on the chart.
 *    The rental path read the IMMUTABLE input owner; it now reads the mutable
 *    holding, which is what the business-dividend path already did.
 *  - IM-F5-01: the survivor inherited the deceased's ORIGINAL base cost, so a
 *    later sale was taxed on gains that died with him. Every CGT-bearing
 *    holding — GIA, tracked holdings, property — now rebases to value at death.
 *  - FG-F6-04: `ownerId` and `onDeath` on a liability. A sole debt is settled
 *    from the estate by default, a joint one continues, and the engine says
 *    which it applied rather than clearing anything silently.
 *  - FG-F3-04: `ownerId` on a spending row and a household
 *    `survivorSpendingFactorBps`. An owned row stops with its owner; a joint
 *    row scales by the adviser's STATED factor. With no factor the joint figure
 *    carries on exactly as before and `survivor-spending-unmodelled` is raised
 *    — a default here would be the tool inventing the survivor's cost of living.
 *
 * No figure moves on any existing golden: the ten fixtures have no sole let, no
 * sole debt and no stated factor, and neither death fixture realises a gain
 * after the death. `retiree-drawdown` and `stress-path` gain two warnings, and
 * the additive proofs strip them under the terms in `how-to-verify.md`.
 *
 * That is also the gap the register named, so an ELEVENTH fixture
 * (`survivor-path`) runs a couple through a first death with all four biting at
 * once. The 3.15.0 goldens are archived under `golden/v3.15.0/`. Tests:
 * survivor-path.
 */
/**
 * 3.17.0 — GOAL 2026-09-05 A2.5: the FIRST of two modelled deaths is assessed
 * in its own right, and `estimatedIht.firstDeath` carries it.
 *
 * ADDITIVE on every existing figure — all eleven goldens keep their headline
 * numbers and only their hashes move — but a bump all the same, and the reason
 * is the point: 3.16.0 is already on `main`, so leaving the label alone would
 * have two materially different serialised outputs stamped 3.16.0, and a stored
 * run's version would no longer identify its shape. The 3.16.0 goldens are
 * archived under `golden/v3.16.0/`.
 *
 * What it assesses is the DECEASED's estate — their own holdings plus half of
 * anything joint, less what they owed — captured in the death step before the
 * transfer, because by the time the year row is written every asset belongs to
 * whoever survived. The first cut read the household's `totalNetWorth` and
 * charged a widow's own money to her husband's estate.
 *
 * Lifetime gifts are NOT charged here: every gift in this plan is the
 * household's, so charging them at the first death attributes all of them to
 * whoever died first. They stay where the plan has always assessed them —
 * against the final estate — and the report says so.
 *
 * What the first death leaves unused is a PROPORTION of its own bands (IHTA
 * 1984 s.8A; s.8G for the residence band), truncated on the unused share so the
 * second estate is never credited with a fraction of a band it did not inherit.
 * Tests: first-death-assessment, iht-first-death-report.
 */
/**
 * B2.1 (GOAL 2026-09-05) — `trusts[]` on the input, and an
 * `arrangement-not-modelled` warning. **DELIBERATELY NOT a version bump**, and
 * the reasoning matters because 3.17.0 bumped for less.
 *
 * 3.17.0's bump was needed because a field was ADDED TO THE OUTPUT: two
 * materially different serialised shapes would otherwise have carried one
 * label, and a stored run's version would stop identifying its shape. Here the
 * new field is INPUT-only. No output shape changes, no figure moves on any
 * fixture, and the extra warning can only appear on a plan that records an
 * arrangement — which no run stored under 3.17.0 can have done, because the
 * field did not exist. So no two runs stamped 3.17.0 can differ in shape.
 *
 * Recorded here rather than left as a silence, per the precedent in
 * `docs/DECISIONS.md`. Neutrality is proved over all eleven golden fixtures in
 * `trusts-output-neutral.test.ts`.
 */
/**
 * B2.3 (GOAL 2026-09-05) — `fromTrust` on an income stream, and a note saying
 * what the plan has not modelled about the trust's own tax. **DELIBERATELY NOT
 * a version bump**, on B2.1's reasoning rather than B2.2's: the field is
 * INPUT-only, no output shape changes, no calculation changes, every golden is
 * untouched, and the new warning can only appear on a plan that records a
 * distribution — which no run stored under 3.17.0 can have done, because the
 * schema did not accept the field.
 *
 * Recorded here rather than left as a silence, per the precedent B2.1 set. The
 * review found the decision was sound and written nowhere the next reader would
 * look.
 */
/**
 * 3.18.0 (2026-09-11) — GOAL 2026-09-10 item 12: estate structures the plan
 * cannot represent are NAMED before an apparently complete figure is read.
 * Six warnings, no figure moves on any fixture, and every golden keeps its
 * headline numbers:
 *  - `iht-death-assessed-on-household`: a couple with ONE death modelled has
 *    its headline made on the whole household's wealth; the note reconciles
 *    the deceased's own share and the survivor's, to the pound.
 *  - `iht-first-death-tax-not-deducted`: a charged first death estimates a tax
 *    and then passes everything to the survivor with nothing taken out.
 *  - `iht-business-relief-not-recorded`: a business interest with no relief
 *    position recorded is counted in full (a recorded 0% raises nothing).
 *  - `db-pension-survivor-fraction-unrecorded`: a DB pension with no survivor's
 *    share recorded stops at the member's death.
 *  - `business-income-stops-at-death`: salary or dividends from a business that
 *    passes to the survivor stop while the business carries on.
 *  - `gift-into-trust-not-modelled`: a gift beside a trust the client settled
 *    is assessed as an outright gift, not a chargeable lifetime transfer.
 * Plus one sentence on the B2.1 gate where the client is the beneficiary of an
 * interest in possession trust.
 *
 * A bump, on 3.16.0's reasoning rather than B2.1's: two of these land on
 * EXISTING inputs (`business-owner-insolvent`, `survivor-path`), so a stored
 * 3.17.0 run and a fresh one would differ in shape under one label. The 3.17.0
 * goldens are archived under `golden/v3.17.0/`. Tests: estate-scope.
 */
/*
 * 3.19.0 — GOAL 2026-09-16 inheritance tax explained (IE-1..IE-3):
 *  - `estimatedIht.workingByYear`: the whole working for every projection year
 *    (reconciliation from household net worth, composition with asset ids,
 *    reliefs, the allowance breakdown, estate/gift/total tax, status, gifts),
 *    with its identities asserted before it is emitted;
 *  - the residence band's taper is tested on the estate after debts and BEFORE
 *    business relief (it was tested after relief) — moves a figure only where
 *    relief is taken and the pre-relief estate crosses £2m;
 *  - `iht.residenceBasis` (not entered / does not apply / projected main
 *    residence net of its linked mortgage / fixed amount) and per-band transfer
 *    percentages; new warning `iht-main-residence-not-recorded`.
 * No archived fixture moves (`golden-masters.additive.test.ts`, 3.18.0 block).
 */
/*
 * NO bump — meeting-notes remediation (2026-09-18, spec 2026-09-17 §5 N20).
 * The ADAPTER (`wire-mapping.ts`) now accepts a dependant known only by a
 * stated age (`ageEvidence`, no `dateOfBirth`) and hands the engine the
 * disclosed modelling date `dependentModellingDateOfBirth` derives — anchored
 * to the evidence's own date, so the input is deterministic. The engine's own
 * `DependentInput.dateOfBirth` is unchanged and still required; no calculation
 * changed; every golden is byte-identical (1061/1061); and no run stored under
 * 3.19.0 before this change can contain an age-only dependant, because the
 * document schema did not accept one.
 *
 * On B2.1's reasoning (input-only, no output shape change), recorded here so
 * it is not a silence. Reproducibility: a run carrying an age-only dependant
 * is keyed by `codeVersion` as well as `engineVersion` — the build that
 * derived its modelling date is the one that can replay it, which is already
 * what every stored run records beside the engine version (CLAUDE.md "Store
 * input snapshot + … engine version + code version with every projection run").
 */
export const ENGINE_VERSION = '3.19.0';
//# sourceMappingURL=version.js.map